"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { BannerBlock as BannerBlockData } from "@/payload-types";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { cn } from "@/utilities/core/cn";
import { Info, AlertTriangle, XCircle, CheckCircle, type LucideIcon } from "lucide-react";
interface BannerBlockProps {
  block: BannerBlockData;
  className?: string;
}
type BannerStyle = BannerBlockData["style"];
type IconStyleConfig = {
  container: string;
  icon: string;
  iconBg: string;
  Icon: LucideIcon;
};
type QuoteStyleConfig = {
  container: string;
  isQuote: true;
};
type PlainStyleConfig = {
  container: string;
  isPlain: true;
};
type StyleConfig = IconStyleConfig | QuoteStyleConfig | PlainStyleConfig;
const STYLE_CONFIG: Record<BannerStyle, StyleConfig> = {
  info: {
    container: "bg-sky-500/10 border-sky-500/30",
    icon: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-500/15",
    Icon: Info
  },
  warning: {
    container: "bg-amber-500/10 border-amber-500/30",
    icon: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-500/15",
    Icon: AlertTriangle
  },
  error: {
    container: "bg-rose-500/10 border-rose-500/30",
    icon: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-500/15",
    Icon: XCircle
  },
  success: {
    container: "bg-emerald-500/10 border-emerald-500/30",
    icon: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-500/15",
    Icon: CheckCircle
  },
  plain: {
    container: "bg-muted/50 border-border",
    isPlain: true
  },
  quote: {
    container: "bg-muted/60 border-l-4 border-foreground/20",
    isQuote: true
  }
};
function isIconStyle(config: StyleConfig): config is IconStyleConfig {
  return "Icon" in config;
}
function isQuoteStyle(config: StyleConfig): config is QuoteStyleConfig {
  return "isQuote" in config;
}

/**
 * Renders a banner/callout block from the article content.
 * Supports info, warning, error, success, plain, and quote styles.
 */
export function BannerBlock(t0) {
  const $ = _c(37);
  const {
    block,
    className
  } = t0;
  const config = STYLE_CONFIG[block.style] || STYLE_CONFIG.info;
  if (isQuoteStyle(config)) {
    let t1;
    if ($[0] !== className || $[1] !== config.container) {
      t1 = cn("mb-6 rounded-lg py-4 px-6", config.container, className);
      $[0] = className;
      $[1] = config.container;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== block.content) {
      t2 = <div className="text-foreground/90 [&_p]:m-0"><RichText data={block.content} /></div>;
      $[3] = block.content;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t1 || $[6] !== t2) {
      t3 = <aside className={t1} role="note">{t2}</aside>;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (!isIconStyle(config)) {
    let t1;
    if ($[8] !== className || $[9] !== config.container) {
      t1 = cn("mb-6 rounded-lg border p-4", config.container, className);
      $[8] = className;
      $[9] = config.container;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== block.content) {
      t2 = <div className="text-sm text-foreground/90 [&_p]:m-0"><RichText data={block.content} /></div>;
      $[11] = block.content;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== t1 || $[14] !== t2) {
      t3 = <aside className={t1} role="note">{t2}</aside>;
      $[13] = t1;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    return t3;
  }
  const {
    Icon
  } = config;
  let t1;
  if ($[16] !== className || $[17] !== config.container) {
    t1 = cn("mb-6 rounded-lg border p-4", config.container, className);
    $[16] = className;
    $[17] = config.container;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== config.iconBg) {
    t2 = cn("shrink-0 rounded-lg p-1.5", config.iconBg);
    $[19] = config.iconBg;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] !== config.icon) {
    t3 = cn("size-5", config.icon);
    $[21] = config.icon;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== Icon || $[24] !== t3) {
    t4 = <Icon className={t3} />;
    $[23] = Icon;
    $[24] = t3;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t2 || $[27] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[26] = t2;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== block.content) {
    t6 = <div className="text-sm text-foreground/90 [&_p]:m-0"><RichText data={block.content} /></div>;
    $[29] = block.content;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== t5 || $[32] !== t6) {
    t7 = <div className="flex gap-3 items-center">{t5}{t6}</div>;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t1 || $[35] !== t7) {
    t8 = <aside className={t1} role="note">{t7}</aside>;
    $[34] = t1;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
}